import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_BASE_URL } from '../config';


export const fetchBlockRules = createAsyncThunk(
  'blockRule/fetchBlockRules',
  async (adminId, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/block-rules?adminId=${adminId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch block rules');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch block rules');
    }
  }
);

export const createBlockRule = createAsyncThunk(
  'blockRule/createBlockRule',
  async (ruleData, { rejectWithValue }) => {
    try {
      const payload = {
        adminId: ruleData.adminId,
        type: ruleData.blockType === 'Website' ? 'website' : 'app',
        target: ruleData.target,
        applyToAll: ruleData.applyTo === 'All Employees',
        appliedDevices: ruleData.appliedDevices || []
      };

      const response = await fetch(`${API_BASE_URL}/block-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create block rule');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create block rule');
    }
  }
);

export const updateBlockRule = createAsyncThunk(
  'blockRule/updateBlockRule',
  async (ruleData, { rejectWithValue }) => {
    try {
      const payload = {
        adminId: ruleData.adminId,
        type: ruleData.blockType === 'Website' ? 'website' : 'app',
        target: ruleData.target,
        applyToAll: ruleData.applyTo === 'All Employees',
        appliedDevices: ruleData.appliedDevices || []
      };

      const response = await fetch(`${API_BASE_URL}/block-rules/${ruleData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update block rule');
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update block rule');
    }
  }
);

export const deleteBlockRule = createAsyncThunk(
  'blockRule/deleteBlockRule',
  async ({ id, adminId }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/block-rules/${id}?adminId=${adminId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete block rule');
      }
      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete block rule');
    }
  }
);

const blockRuleSlice = createSlice({
  name: 'blockRule',
  initialState: {
    rules: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlockRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlockRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
      })
      .addCase(fetchBlockRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createBlockRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBlockRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules.unshift(action.payload);
      })
      .addCase(createBlockRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateBlockRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBlockRule.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rules.findIndex(rule => (rule._id || rule.id) === (action.payload._id || action.payload.id));
        if (index !== -1) {
          state.rules[index] = action.payload;
        }
      })
      .addCase(updateBlockRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteBlockRule.fulfilled, (state, action) => {
        state.rules = state.rules.filter((rule) => (rule._id || rule.id) !== action.payload);
      });
  }
});

export default blockRuleSlice.reducer;
